import api from './api'
import type { AxiosRequestConfig } from 'axios'
import type {
  ToolConfigureRequest,
  ToolConfigureResponse,
  ToolTestConnectionRequest,
  ToolTestConnectionResponse,
  MCPCapabilitiesResponse,
} from '@/schemas/tool-config'

// Types
export interface Tool {
  id: string
  organization_id: string
  name: string
  description: string | null
  tool_type: string
  category: string
  config: Record<string, unknown>
  is_active: boolean
  is_configured: boolean
  agent_count: number
  created_at: string
  updated_at: string
}

export interface ToolListParams {
  search?: string
  category?: string
  is_active?: boolean
  page?: number
  page_size?: number
}

export interface ToolListResponse {
  tools: Tool[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface ToolsByCategoryResponse {
  category: string
  category_label: string
  tools: Tool[]
  count: number
}

// Tool categories
export type ToolCategory = 'communication' | 'productivity' | 'development' | 'search' | 'custom'

export const TOOL_CATEGORIES: ToolCategory[] = [
  'communication',
  'productivity',
  'development',
  'search',
  'custom',
]

// Tool service
export const toolsService = {
  // List tools with pagination and filters
  async listTools(
    orgId: string,
    params?: ToolListParams,
    config?: AxiosRequestConfig
  ): Promise<ToolListResponse> {
    const response = await api.get<ToolListResponse>(
      `/organizations/${orgId}/tools`,
      { params, ...config }
    )
    return response.data
  },

  // List tools grouped by category
  async listByCategory(
    orgId: string,
    config?: AxiosRequestConfig
  ): Promise<ToolsByCategoryResponse[]> {
    const response = await api.get<ToolsByCategoryResponse[]>(
      `/organizations/${orgId}/tools/by-category`,
      config
    )
    return response.data
  },

  // Get a single tool by ID
  async getTool(
    orgId: string,
    toolId: string,
    config?: AxiosRequestConfig
  ): Promise<Tool> {
    const response = await api.get<Tool>(
      `/organizations/${orgId}/tools/${toolId}`,
      config
    )
    return response.data
  },

  // Configure a tool with credentials
  async configureTool(
    orgId: string,
    toolId: string,
    request: ToolConfigureRequest,
    config?: AxiosRequestConfig
  ): Promise<ToolConfigureResponse> {
    const response = await api.put<ToolConfigureResponse>(
      `/organizations/${orgId}/tools/${toolId}/configure`,
      request,
      config
    )
    return response.data
  },

  // Test tool connection
  async testToolConnection(
    orgId: string,
    toolId: string,
    request?: ToolTestConnectionRequest,
    config?: AxiosRequestConfig
  ): Promise<ToolTestConnectionResponse> {
    const response = await api.post<ToolTestConnectionResponse>(
      `/organizations/${orgId}/tools/${toolId}/test`,
      request || {},
      config
    )
    return response.data
  },

  // Discover MCP server capabilities
  async discoverCapabilities(
    orgId: string,
    toolId: string,
    request?: ToolTestConnectionRequest,
    config?: AxiosRequestConfig
  ): Promise<MCPCapabilitiesResponse> {
    const response = await api.post<MCPCapabilitiesResponse>(
      `/organizations/${orgId}/tools/${toolId}/discover-capabilities`,
      request || {},
      config
    )
    return response.data
  },
}
